// @/components/reusable/StepNavigator.jsx
// V2 Step Navigator — shared multi-step progress bar with dual-color indicator
//
// Used by: carbon-accounting, self-assessment, first-run, and any future multi-step flow.

import React from "react";
import * as motion from "motion/react-client";
import { Badge } from "@/components/reusable/badge";

/**
 * StepNavigator V2 Component
 *
 * Renders a dual-color animated progress bar (black = current, blue = completed)
 * with labelled step badges below.
 *
 * @param {Array}    props.steps              - Array of { id, name } objects
 * @param {number}   props.currentStep        - Active step index (0-based)
 * @param {Function} props.onStepClick        - Called with step index on click
 * @param {Function} [props.canNavigateToStep] - Optional: (index) => boolean. If false and step is clicked, onDisabledStepClick is called instead
 * @param {Function} [props.onDisabledStepClick] - Optional: (index) => void. Called when a disabled step is clicked
 * @param {boolean}  [props.allComplete]      - When true, all steps show as completed (blue)
 * @param {string}   [props.className]        - Additional wrapper classes
 */
const StepNavigator = ({ 
  steps, 
  currentStep, 
  onStepClick, 
  canNavigateToStep,
  onDisabledStepClick,
  allComplete = false, 
  className = "" 
}) => {
  // Each step fills its proportional share: step 0 = 33%, step 1 = 66%, step 2 = 100%
  const progressPercentage =
    allComplete ? 100 : steps.length <= 1 ? 100 : ((currentStep + 1) / steps.length) * 100;
  // Completed portion (steps before current, or all if allComplete)
  const completedPercentage =
    allComplete ? 100 : steps.length <= 1 ? 0 : (currentStep / steps.length) * 100;

  const springConfig = {
    type: "spring",
    damping: 26,
    stiffness: 300,
    mass: 0.5,
    bounce: 0.25,
    visualDuration: 0.48,
  };

  return (
    <div className={`mb-10 ${className}`}>
      <h4 className="sr-only">Progress</h4>

      <div aria-hidden="true">
        {/* Animated progress bar — black for current, blue overlay for completed */}
        <div className="relative overflow-hidden rounded-full bg-zinc-100 h-1.5">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={springConfig}
            className="absolute inset-y-0 left-0 rounded-full bg-black"
          />
          {(currentStep > 0 || allComplete) && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${completedPercentage}%` }}
              transition={springConfig}
              className="absolute inset-y-0 left-0 rounded-full bg-primary"
            />
          )}
        </div>

        {/* Mobile: show current step name only */}
        <div className="mt-3 flex items-center justify-center gap-1.5 sm:hidden text-sm">
          <Badge color={allComplete ? "blue" : "black"} size="md">
            {steps[currentStep]?.id}
          </Badge>
          <span className="font-medium text-black truncate">
            {steps[currentStep]?.name}
          </span>
        </div>

        {/* Tablet+Desktop: step labels grid — smaller on sm, full size on lg+ */}
        <div className="mt-4 hidden sm:grid" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
          {steps.map((step, index) => {
            const isActive = !allComplete && index === currentStep;
            const isCompleted = allComplete || index < currentStep;

            // Colors: completed = blue, active = black, inactive = zinc
            const textColor = isCompleted ? "text-primary" : isActive ? "text-black" : "text-zinc-400";
            const badgeColor = isCompleted ? "blue" : isActive ? "black" : "zinc";

            // Alignment: first left, last right, middle centered
            const justifyClass =
              index === 0 ? "justify-start" :
                index === steps.length - 1 ? "justify-end" :
                  "justify-center";

            const handleStepClick = (e) => {
              // If canNavigateToStep is provided, check if navigation is allowed
              if (canNavigateToStep && !canNavigateToStep(index)) {
                // Navigation not allowed - call disabled handler if provided, passing the event
                onDisabledStepClick?.(index, e);
                return;
              }
              // Navigation allowed - proceed normally
              onStepClick(index);
            };

            return (
              <h4
                key={step.id || index}
                role="button"
                tabIndex={0}
                className={`text-xs/5 lg:text-base/6 flex items-center gap-1 lg:gap-1.5 ${justifyClass} ${textColor} cursor-pointer hover:opacity-80 transition-opacity`}
                onClick={(e) => handleStepClick(e)}
                onKeyDown={(e) => e.key === "Enter" && handleStepClick(e)}
              >
                <Badge color={badgeColor} size="sm" className="lg:text-xs lg:size-5">
                  {step.id}
                </Badge>
                <span className="truncate">{step.name}</span>
              </h4>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepNavigator;
